import { useLocation, useNavigate } from 'react-router-dom';
import { useTenant } from '../context/TenantContext.js';
import { useAuth } from '../context/AuthContext.js';
import { useState, useRef, useEffect, useMemo } from 'react';

const PAGE_TITLES: Record<string, string> = {
  '/': 'Dashboard',
  '/users': 'Users',
  '/plans': 'Plans',
  '/payments': 'Payments',
  '/embeds': 'Embeds',
  '/affiliates': 'Affiliates',
  '/settings': 'Settings',
  '/guide': 'Guide',
  '/admin/tenants': 'All Servers',
  '/admin/users': 'All Users',
  '/admin/whitelist': 'Whitelist',
  '/admin/payments': 'All Payments',
  '/admin/roles': 'Role Assignments',
};

export function Topbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { tenants, activeTenant, selectTenant } = useTenant();
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [search, setSearch] = useState('');
  const selectorRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const isAdmin = user?.role === 'platform_admin';
  const title = PAGE_TITLES[location.pathname] ?? 'Dashboard';
  const hasMultiple = tenants.length > 1;
  const showSearch = tenants.length > 5;

  const filtered = useMemo(() => {
    if (!search.trim()) return tenants;
    const q = search.toLowerCase();
    return tenants.filter(
      (t) => t.name.toLowerCase().includes(q) || t.guildId.includes(q),
    );
  }, [tenants, search]);

  // close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(e.target as Node)) {
        setSelectorOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // focus search when dropdown opens
  useEffect(() => {
    if (selectorOpen && showSearch) {
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [selectorOpen, showSearch]);

  return (
    <div className="topbar">
      <div className="topbar-left">
        <h1>{title}</h1>
        {activeTenant && (
          <div className="tenant-selector-wrap" ref={selectorRef}>
            <div
              className="server-badge"
              onClick={() => hasMultiple && setSelectorOpen(!selectorOpen)}
              style={{ cursor: hasMultiple ? 'pointer' : 'default' }}
            >
              <div className={`server-dot ${activeTenant.botActive ? '' : 'inactive'}`} />
              {activeTenant.name}
              {isAdmin && (
                <span style={{
                  fontSize: 9,
                  padding: '1px 5px',
                  borderRadius: 3,
                  background: 'rgba(155,89,182,0.15)',
                  color: 'var(--violet)',
                  marginLeft: 6,
                  fontWeight: 600,
                  letterSpacing: '0.03em',
                }}>
                  ADMIN
                </span>
              )}
              {hasMultiple && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 12, height: 12, marginLeft: 2 }}>
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              )}
            </div>
            {selectorOpen && hasMultiple && (
              <div className="tenant-dropdown" style={{ maxHeight: 320, overflowY: 'auto' }}>
                {showSearch && (
                  <div style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>
                    <input
                      ref={searchRef}
                      className="form-input"
                      type="text"
                      placeholder="Search servers..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      style={{ fontSize: 12, padding: '5px 8px', width: '100%' }}
                    />
                  </div>
                )}
                {filtered.length === 0 && (
                  <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--t3)' }}>
                    No servers match "{search}"
                  </div>
                )}
                {filtered.map((t) => (
                  <div
                    key={t.id}
                    className={`tenant-dropdown-item ${t.id === activeTenant.id ? 'active' : ''}`}
                    onClick={() => {
                      void selectTenant(t.id).then((ok) => {
                        if (ok) {
                          setSelectorOpen(false);
                          setSearch('');
                          if (location.pathname.startsWith('/admin')) {
                            navigate('/');
                          }
                        }
                      });
                    }}
                  >
                    <div className={`server-dot ${t.botActive ? '' : 'inactive'}`} />
                    <span style={{ flex: 1 }}>{t.name}</span>
                    {!t.botActive && (
                      <span style={{ fontSize: 9, color: 'var(--t3)', marginLeft: 4 }}>offline</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="topbar-actions">
        <button
          className="btn-sm btn-ghost"
          onClick={() => navigate('/settings')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: 15, height: 15, verticalAlign: 'middle', marginRight: 4 }}>
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4" />
          </svg>
          Settings
        </button>
      </div>
    </div>
  );
}
